import * as React from "react";
import styled from "styled-components";
import { theme } from "../../theme";
import { useLookup } from "../../graphql/hooks/useLookup";
import Modal from "../molecules/Modal";
import { HorizontalSpacer, VerticalSpacer } from "../atoms/Spacers";
import { getClientTypeIcon } from "../../utils/getClientTypeIcon";
import Body from "../atoms/typography/Body";
import BodySmall from "../atoms/typography/BodySmall";
import OutlinedButton from "../atoms/buttons/OutlinedButton";
import { useTranslation } from "react-i18next";
import { LookupIcon } from "../atoms/icons/LookupIcon";
import { CrossIcon } from "../atoms/icons/CrossIcon";
import FilterChip from "../atoms/inputs/FilterChip";
import { useExternalLookup } from "../../graphql/hooks/useExternalLookup";
import { Row } from "react-grid-system";
import type { LookupCompany } from "../../graphql/queries/getLookupCompany";
import type {
  ExternalLookupCompany,
  IdentifierType,
} from "../../graphql/queries/getExternalLookupCompany";
import RadioButton from "../atoms/inputs/RadioButton";

interface Props {
  type: "modal" | "dropdown" | "dropdownSmall";
  onClickResult: (item: LookupCompany) => void;
  onClear?: () => void;
  placeholder?: string;
  variant?: "default" | "light";
  displayOnlyIssuer?: boolean;
  displayOnlyRecipient?: boolean;
  preselectedValue?: string;
  defaultSource?: "internal" | "external";
  canToggleSources?: boolean;
}

const LookupTextField = styled.input<{ $isModal?: boolean }>`
  border: none;
  padding: 8px;
  width: 100%;
  font-size: 18px;
  font-family: Inter, sans-serif;
  color: ${theme.colors.text};
  background-color: transparent;
  border: none;
  border-bottom: ${(props) =>
    props.$isModal ? `1.25px solid ${theme.colors.grey40}` : "none"};

  &:focus-visible {
    outline: none;
  }
`;
const LookupTextFieldSmall = styled.input<{ variant?: "default" | "light" }>`
  border: none;
  padding: 8px 8px 8px 4px;
  border-bottom: ${`1.25px solid ${theme.colors.grey40}`};
  width: 100%;
  min-width: 240px;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: ${(props) =>
    props.variant === "light" ? theme.colors.textMuted : theme.colors.text};
  background-color: ${(props) =>
    props.variant === "light" ? "transparent" : "white"};

  &::placeholder {
    color: ${(props) =>
      props.variant === "light"
        ? theme.colors.textSecondary
        : theme.colors.textMuted};
  }

  &:focus-visible {
    outline: none;
  }
`;

const LookupIconWrapper = styled.div`
  position: absolute;
  right: 6px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const ResponseContainer = styled.div`
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  align-items: center;

  &:hover {
    background-color: ${theme.colors.hover};
  }
`;

const Highlight = styled.span`
  color: ${theme.colors.info};
  font-weight: bold;
`;

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const ResultsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1.25px solid ${theme.colors.grey40};
  border-radius: 4px;
  margin-top: 4px;
  overflow-y: auto;
  z-index: 1000;
`;

const highlightMatch = (text: string, searchValue: string): React.ReactNode => {
  if (!searchValue) return text;

  const regex = new RegExp(`(${searchValue})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? <Highlight key={index}>{part}</Highlight> : part,
  );
};

const Lookup: React.FC<Props> = ({
  type,
  onClickResult,
  onClear,
  placeholder,
  variant,
  displayOnlyIssuer,
  displayOnlyRecipient,
  preselectedValue,
  defaultSource = "internal",
  canToggleSources,
}) => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [activeSearchValue, setActiveSearchValue] = React.useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [hasSelection, setHasSelection] = React.useState(false);
  const [source, setSource] = React.useState<"internal" | "external">(
    defaultSource,
  );
  const [identifierType, setIdentifierType] = React.useState<
    IdentifierType | undefined
  >(undefined);

  React.useEffect(() => {
    setSearchValue(preselectedValue ?? "");
    setHasSelection(!!preselectedValue);
  }, [preselectedValue]);

  const {
    lookup: data,
    loading,
    error,
    previousData,
  } = useLookup({
    searchTerm: activeSearchValue,
    skip: 0,
    take: type === "modal" ? 10 : 5,
    isIssuer: displayOnlyIssuer,
    isRecipient: displayOnlyRecipient,
  });

  const isExternalSourceActive = type === "modal" && source === "external";

  const {
    lookup: externalLookupData,
    loading: externalLoading,
    error: externalError,
    previousData: externalPreviousData,
  } = useExternalLookup(
    {
      searchTerm: activeSearchValue,
      skip: 0,
      take: 10,
      identifierType: identifierType,
      sortBy: "NAME",
    },
    !isExternalSourceActive,
  );

  const lookup = data ?? previousData?.lookup;
  const externalLookup = externalLookupData ?? externalPreviousData?.lookup;

  const activeItems: Array<LookupCompany | ExternalLookupCompany> =
    React.useMemo(
      () =>
        (isExternalSourceActive
          ? externalLookup?.externalQuickLookup?.items
          : lookup?.quickcompany?.items) ?? [],
      [isExternalSourceActive, externalLookup, lookup],
    );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (activeItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => Math.min(prev + 1, activeItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const focusedItem =
        focusedIndex >= 0 ? activeItems[focusedIndex] : undefined;
      if (focusedItem) onSelectItem(focusedItem);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setModalOpen(false);
    }
  };

  React.useEffect(() => {
    setFocusedIndex(0);
  }, [activeItems]);

  const onSelectItem = (item: LookupCompany | ExternalLookupCompany) => {
    let selectedItem: LookupCompany = item as LookupCompany;

    if (isExternalSourceActive) {
      selectedItem = {
        clientIdentifier: (item as ExternalLookupCompany).identifier,
        clientType: "UNKNOWN",
        companyName: (item as ExternalLookupCompany).name,
        id: (item as ExternalLookupCompany).id,
      };
    }
    setActiveSearchValue("");
    setHasSelection(type !== "modal");
    onClickResult(selectedItem);
    setModalOpen(false);
    setIsDropdownOpen(false);
  };

  const onClearSelection = () => {
    setSearchValue("");
    setActiveSearchValue("");
    setHasSelection(false);
    onClear?.();
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onSearch = (value: string) => {
    setIsDropdownOpen(value.length >= 2);
    setSearchValue(value);
    setHasSelection(false);
    if (value.length >= 2) {
      setTimeout(() => {
        setActiveSearchValue(value);
      }, 200);
    }
    if (value.length === 0) {
      setActiveSearchValue("");
    }
  };

  return (
    <div>
      {type === "modal" && modalOpen && (
        <Modal onClose={() => setModalOpen(false)} closeIcon width={800}>
          <div>
            <LookupTextField
              placeholder={t("lookup.searchCompany")}
              onChange={(e) => onSearch(e.target.value)}
              value={searchValue}
              onKeyDown={handleKeyDown}
              autoFocus
              $isModal
            />
            <HorizontalSpacer spacing={1} />
            {canToggleSources && (
              <div style={{ display: "flex", gap: "8px" }}>
                <FilterChip
                  titleKey="random.internal"
                  selected={source === "internal"}
                  onSelect={() => setSource("internal")}
                />
                <FilterChip
                  titleKey="random.external"
                  selected={source === "external"}
                  onSelect={() => setSource("external")}
                />
                {source === "external" && (
                  <>
                    <RadioButton
                      title="Bolag"
                      checked={identifierType === "ORGANIZATION_NUMBER"}
                      onChange={() => setIdentifierType("ORGANIZATION_NUMBER")}
                    />
                    <RadioButton
                      title="Person"
                      checked={identifierType === "PERSON_IDENTIFIER"}
                      onChange={() => setIdentifierType("PERSON_IDENTIFIER")}
                    />
                    <RadioButton
                      title="Alla"
                      checked={identifierType === undefined}
                      onChange={() => setIdentifierType(undefined)}
                    />
                  </>
                )}
              </div>
            )}
            <HorizontalSpacer spacing={1} />
            {(loading || externalLoading) && <div />}
            {source === "internal" &&
              lookup?.quickcompany?.items?.map((item, index) => (
                <ResponseContainer
                  key={item.clientIdentifier}
                  onClick={() => onSelectItem(item)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSelectItem(item);
                  }}
                  style={{
                    backgroundColor:
                      focusedIndex === index
                        ? theme.colors.hover
                        : "transparent",
                  }}
                >
                  {getClientTypeIcon(item.clientType)}
                  <div>
                    <Body>{highlightMatch(item.companyName, searchValue)}</Body>
                    <BodySmall>
                      {highlightMatch(item.clientIdentifier, searchValue)}
                    </BodySmall>
                  </div>
                </ResponseContainer>
              ))}
            {source === "external" &&
              externalLookup?.externalQuickLookup?.items?.map((item, index) => (
                <ResponseContainer
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onSelectItem(item);
                  }}
                  style={{
                    backgroundColor:
                      focusedIndex === index
                        ? theme.colors.hover
                        : "transparent",
                  }}
                >
                  {getClientTypeIcon(item.identifierType)}
                  <div style={{ width: "100%" }}>
                    <Row nogutter justify="between" align="end">
                      <Body>{highlightMatch(item.name, searchValue)}</Body>
                      <BodySmall color={theme.colors.textMuted}>
                        {item.address}
                      </BodySmall>
                    </Row>
                    <BodySmall>
                      {highlightMatch(item.identifier, searchValue)}
                    </BodySmall>
                  </div>
                </ResponseContainer>
              ))}

            {searchValue &&
              searchValue.length > 2 &&
              (error || externalError) && (
                <Body color={theme.colors.error}>
                  Något gick fel, försök igen eller kontakta support.
                </Body>
              )}

            <HorizontalSpacer spacing={1} />
            <BodySmall color={theme.colors.textMuted}>
              <b>esc</b> för att stänga
            </BodySmall>
          </div>
        </Modal>
      )}

      {type === "modal" && (
        <OutlinedButton
          title="Lookup"
          onClick={() => {
            setModalOpen(true);
            if (searchValue) {
              setActiveSearchValue(searchValue);
            }
          }}
          icon={<LookupIcon />}
          color={theme.colors.primary}
        />
      )}

      {type === "dropdown" && (
        <DropdownWrapper ref={wrapperRef}>
          <LookupTextField
            placeholder={t("lookup.searchCompany")}
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <LookupIconWrapper>
            <LookupIcon />
          </LookupIconWrapper>
          {loading && <div />}
          <ResultsDropdown>
            {isDropdownOpen &&
              lookup?.quickcompany?.items?.slice(0, 5).map((item, index) => (
                <ResponseContainer
                  key={item.clientIdentifier}
                  onClick={() => onSelectItem(item)}
                  style={{
                    backgroundColor:
                      focusedIndex === index ? theme.colors.hover : undefined,
                  }}
                >
                  {getClientTypeIcon(item.clientType)}
                  <div>
                    <Body>{highlightMatch(item.companyName, searchValue)}</Body>
                    <BodySmall>
                      {highlightMatch(item.clientIdentifier, searchValue)}
                    </BodySmall>
                  </div>
                </ResponseContainer>
              ))}
          </ResultsDropdown>
        </DropdownWrapper>
      )}
      {type === "dropdownSmall" && (
        <DropdownWrapper ref={wrapperRef}>
          <LookupTextFieldSmall
            placeholder={placeholder ?? t("lookup.searchCompany")}
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            variant={variant}
          />
          <LookupIconWrapper>
            {hasSelection && onClear && (
              <div
                style={{
                  cursor: "pointer",
                  pointerEvents: "all",
                  display: "flex",
                  transform: "scale(0.8)",
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onClearSelection();
                }}
              >
                <CrossIcon color={theme.colors.textSecondary} />
                <VerticalSpacer spacing={0.5} />
              </div>
            )}
            <LookupIcon />
          </LookupIconWrapper>
          {loading && <div />}
          {isDropdownOpen &&
            lookup &&
            lookup?.quickcompany?.items?.slice(0, 5).length > 0 && (
              <ResultsDropdown>
                {lookup.quickcompany.items.slice(0, 5).map((item, index) => (
                  <ResponseContainer
                    key={item.clientIdentifier}
                    onClick={() => onSelectItem(item)}
                    style={{
                      backgroundColor:
                        focusedIndex === index ? theme.colors.hover : undefined,
                    }}
                  >
                    {getClientTypeIcon(item.clientType)}
                    <div>
                      <Body>
                        {highlightMatch(item.companyName, searchValue)}
                      </Body>
                      <BodySmall>
                        {highlightMatch(item.clientIdentifier, searchValue)}
                      </BodySmall>
                    </div>
                  </ResponseContainer>
                ))}
              </ResultsDropdown>
            )}
        </DropdownWrapper>
      )}
    </div>
  );
};

export default Lookup;
